import { useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { calcEmi, inr } from "@/lib/format";
import { Target, TrendingUp } from "lucide-react";

function newRound() {
  const amount = (Math.floor(Math.random() * 90) + 10) * 100000; // 10L - 1Cr
  const rate = +(7 + Math.random() * 4).toFixed(2); // 7-11%
  const years = [10, 15, 20, 25, 30][Math.floor(Math.random() * 5)];
  const emi = calcEmi(amount, rate, years);
  return { amount, rate, years, emi };
}

export function EmiGuessGame() {
  const [round, setRound] = useState(newRound);
  const [guess, setGuess] = useState(round.emi * 0.7);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [plays, setPlays] = useState(0);

  const errorPct = Math.abs(guess - round.emi) / round.emi * 100;
  const points = revealed ? Math.max(0, Math.round(100 - errorPct * 2)) : 0;

  const reveal = () => {
    if (revealed) return;
    setRevealed(true);
    setScore((s) => s + Math.max(0, Math.round(100 - errorPct * 2)));
    setPlays((p) => p + 1);
  };
  const next = () => { const r = newRound(); setRound(r); setGuess(r.emi * 0.7); setRevealed(false); };

  const max = Math.round(round.emi * 1.8);

  return (
    <div className="border border-border bg-card p-6 md:p-10" data-testid="emi-game">
      <div className="mb-6 flex items-center justify-between font-mono text-xs uppercase tracking-widest text-muted-foreground">
        <span className="flex items-center gap-2"><Target className="h-4 w-4 text-cyan" /> Guess the EMI</span>
        <span className="text-cyan">Total {score} pts · {plays} rounds</span>
      </div>
      <p className="mb-8 text-sm text-muted-foreground">Train your money intuition. Estimate the monthly EMI, then reveal the true figure. The closer you are, the more points you score.</p>

      <div className="mb-8 grid grid-cols-3 gap-3">
        {[["Loan", inr(round.amount)], ["Rate", round.rate + "%"], ["Tenure", round.years + " yrs"]].map(([l, v]) => (
          <div key={l} className="border border-border bg-background p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{l}</p>
            <p className="mt-1 font-mono text-lg text-foreground">{v}</p>
          </div>
        ))}
      </div>

      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Your guess</span>
        <span className="font-mono text-xl text-cyan" data-testid="emi-game-guess">{inr(guess)}</span>
      </div>
      <Slider value={[guess]} min={1000} max={max} step={500} onValueChange={(v) => setGuess(v[0])} disabled={revealed} data-testid="emi-game-slider" />

      {revealed && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 border border-cyan/40 bg-cyan/5 p-5" data-testid="emi-game-result">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Actual EMI</p>
              <p className="font-mono text-2xl font-bold text-foreground">{inr(round.emi)}</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">You scored</p>
              <p className="flex items-center gap-2 font-mono text-2xl font-bold text-cyan"><TrendingUp className="h-5 w-5" /> +{points}</p>
            </div>
          </div>
          <p className="mt-2 font-mono text-xs text-muted-foreground">Off by {errorPct.toFixed(1)}%</p>
        </motion.div>
      )}

      <div className="mt-6 flex gap-3">
        {!revealed ? (
          <Button onClick={reveal} className="rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90" data-testid="emi-game-reveal">Reveal & Score</Button>
        ) : (
          <Button onClick={next} className="rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90" data-testid="emi-game-next">Next Round</Button>
        )}
      </div>
    </div>
  );
}
